import React, { useState, useEffect } from "react";
import api from "../../services/api";
import PricingModelSelector from "../../components/admin/PricingModelSelector";
import PermissionsSelector from "../../components/admin/PermissionsSelector";
import {
  FiSave,
  FiAlertCircle,
  FiCheck,
  FiClock,
  FiZap,
  FiShield,
  FiTag,
  FiPlus,
  FiTrash,
  FiCopy,
  FiPercent,
  FiActivity,
  FiX,
} from "react-icons/fi";

/**
 * Simplified Payment Configuration Page
 *
 * Unified interface for configuring application pricing:
 * - Select pricing model (Freemium/Pro/Subscription/Token-based)
 * - Configure trial settings
 * - Set prices and token allowances
 * - Manage token enforcement
 * - Manage discount vouchers
 *
 * SINGLE SAVE BUTTON FOR EVERYTHING
 */
function SimplifiedPaymentConfig() {
  // Helper functions for safe string/number formatting
  const safeString = (value) => {
    return value || "";
  };

  const formatModelType = (modelType) => {
    if (!modelType) return "";
    try {
      return modelType.replace(/_/g, " ").toUpperCase();
    } catch (e) {
      return modelType || "";
    }
  };

  const formatCurrency = (amount, currency) => {
    if (!amount || amount === 0) return null;
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "USD",
        minimumFractionDigits: 2,
      }).format(amount / 100);
    } catch (e) {
      return `$${(amount / 100).toFixed(2)}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  const safeNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === "")
      return defaultValue;
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    return isNaN(num) ? "" : num;
  };

  // Application selection
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loadingApps, setLoadingApps] = useState(true);

  // Pricing model configuration
  const [pricingModel, setPricingModel] = useState("");
  const [pricingConfig, setPricingConfig] = useState({
    planName: "",
    intervals: ["monthly"],
    tokenAllowance: "",
    prices: [{ currency: "usd", monthlyAmount: "", amount: "" }],
  });

  // Trial configuration
  const [trialEnabled, setTrialEnabled] = useState(false);
  const [trialDays, setTrialDays] = useState(14);
  const [trialPermissions, setTrialPermissions] = useState([]);

  // Freemium configuration
  const [freemiumPermissions, setFreemiumPermissions] = useState([]);

  // Permissions from auth service (source of truth)
  const [allPermissions, setAllPermissions] = useState([]);
  const [permissionsByCategory, setPermissionsByCategory] = useState({});
  const [aiPermissions, setAiPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Current template (if exists)
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // Vouchers
  const [vouchers, setVouchers] = useState([]);
  const [vouchersLoading, setVouchersLoading] = useState(false);
  const [showVoucherForm, setShowVoucherForm] = useState(false);
  const [newVoucher, setNewVoucher] = useState({
    code: "",
    discountType: "percentage",
    discountValue: 10,
    maxRedemptions: null,
    valid_from: "",
    valid_until: "",
    active: true,
  });
  const [copiedCode, setCopiedCode] = useState(null);

  // Token settings (now part of main config)
  const [tokenEnforcement, setTokenEnforcement] = useState("track_only");
  const [tokenLimit, setTokenLimit] = useState("");
  const [tokenResetPeriod, setTokenResetPeriod] = useState("monthly");

  // AI features (metadata.ai_features) — which permissions consume tokens
  const [metadataAiFeatures, setMetadataAiFeatures] = useState([]);
  const [aiFeatureToAdd, setAiFeatureToAdd] = useState("");

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("pricing"); // pricing, tokens, vouchers

  // Load applications on mount
  useEffect(() => {
    fetchApplications();
  }, []);

  // Load permissions, template, and vouchers when app is selected
  useEffect(() => {
    if (selectedApp) {
      fetchPermissions(selectedApp);
      fetchCurrentTemplate(selectedApp);
      fetchVouchers(selectedApp);
    }
  }, [selectedApp]);

  const fetchApplications = async () => {
    setLoadingApps(true);
    try {
      const response = await api.get("/configuration/applications");
      setApplications(response.data.data || []);
      if (response.data.data?.length > 0) {
        setSelectedApp(response.data.data[0].id);
      }
    } catch (err) {
      console.error("Failed to load applications", err);
      setError("Failed to load applications. Please refresh the page.");
    } finally {
      setLoadingApps(false);
    }
  };

  const fetchPermissions = async (appId) => {
    setLoadingPermissions(true);
    try {
      const response = await api.get(`/applications/${appId}/permissions`);

      if (response.data.success) {
        setAllPermissions(response.data.permissions || []);
        setPermissionsByCategory(response.data.grouped || {});
        setAiPermissions(response.data.ai_features || []);
      } else {
        setAllPermissions([]);
        setPermissionsByCategory({});
        setAiPermissions([]);
      }
    } catch (err) {
      console.error("Failed to load permissions from auth service", err);
      setAllPermissions([]);
      setPermissionsByCategory({});
      setAiPermissions([]);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const fetchCurrentTemplate = async (appId) => {
    setLoadingTemplate(true);
    try {
      const response = await api.get(`/pricing-templates/${appId}/active`);
      const template = response.data.data?.[0] || response.data.data;

      if (template) {
        console.log("Loaded template:", template); // Debug log
        setCurrentTemplate(template);
        setPricingModel(template.model_type || "");
        setTrialEnabled(template.trial_enabled || false);
        setTrialDays(template.trial_days || 14);
        setTrialPermissions(template.trial_permissions || []);
        setFreemiumPermissions(template.freemium_permissions || []);

        const config = template.configuration || {};

        setPricingConfig({
          planName: config.planName || "",
          intervals: config.intervals || ["monthly"],
          tokenAllowance: config.tokenAllowance || "",
          prices: config.prices || [],
        });

        // Load token enforcement settings
        setTokenEnforcement(template.token_enforcement ?? "track_only");
        setTokenResetPeriod(template.token_reset_period || "monthly");
        setTokenLimit(config.tokenLimit || "");

        // Load AI features from metadata
        const savedAiFeatures = template.metadata?.ai_features || [];
        setMetadataAiFeatures(savedAiFeatures);
      }
    } catch (err) {
      // No template exists yet - that's okay
      console.log("No active template found for", appId);
      setCurrentTemplate(null);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const fetchVouchers = async (appId) => {
    setVouchersLoading(true);
    try {
      const response = await api.get(`/vouchers?applicationId=${appId}`);
      setVouchers(response.data.data || []);
    } catch (err) {
      console.error("Failed to load vouchers", err);
      setVouchers([]);
    } finally {
      setVouchersLoading(false);
    }
  };

  const generateVoucherCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const createVoucher = async () => {
    try {
      const voucherData = {
        code: newVoucher.code || generateVoucherCode(),
        applicationId: selectedApp,
        discountType: newVoucher.discountType,
        discountValue: newVoucher.discountValue,
        active: newVoucher.active,
      };
      if (newVoucher.maxRedemptions)
        voucherData.maxRedemptions = newVoucher.maxRedemptions;
      if (newVoucher.valid_from) voucherData.validFrom = newVoucher.valid_from;
      if (newVoucher.valid_until)
        voucherData.validUntil = newVoucher.valid_until;
      await api.post("/vouchers", voucherData);
      setNewVoucher({
        code: "",
        discountType: "percentage",
        discountValue: 10,
        maxRedemptions: null,
        valid_from: "",
        valid_until: "",
        active: true,
      });
      setShowVoucherForm(false);
      fetchVouchers(selectedApp);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to create voucher. Please try again.",
      );
    }
  };

  const deleteVoucher = async (voucherId) => {
    if (!window.confirm("Are you sure you want to delete this voucher?"))
      return;
    try {
      await api.delete(`/vouchers/${voucherId}`);
      fetchVouchers(selectedApp);
    } catch (err) {
      setError("Failed to delete voucher");
    }
  };

  const toggleVoucherActive = async (voucher) => {
    try {
      await api.put(`/vouchers/${voucher.id}`, { active: !voucher.active });
      fetchVouchers(selectedApp);
    } catch (err) {
      setError("Failed to update voucher");
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  /**
   * SINGLE SAVE FUNCTION - Handles everything:
   * - Pricing template
   * - Token settings
   * - All configurations
   */
  const handleSave = async () => {
    // Validation
    if (!pricingModel) {
      setError("Please select a pricing model");
      return;
    }

    if (pricingModel === "freemium" && freemiumPermissions.length === 0) {
      setError("Please select at least one permission for the free tier");
      return;
    }

    if (pricingModel !== "freemium") {
      const firstPrice = pricingConfig.prices?.[0];

      if (
        pricingModel === "pro_one_time" &&
        safeNumber(firstPrice?.amount) <= 0
      ) {
        setError("Please enter a valid price for the Pro plan");
        return;
      }

      if (
        (pricingModel === "subscription" || pricingModel === "token_based") &&
        safeNumber(firstPrice?.monthlyAmount) <= 0
      ) {
        setError("Please enter a valid monthly price");
        return;
      }

      if (
        pricingModel === "token_based" &&
        (!pricingConfig.tokenAllowance ||
          safeNumber(pricingConfig.tokenAllowance) <= 0)
      ) {
        setError("Please enter a valid token allowance");
        return;
      }
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const preparedPricingConfig = {
        planName: pricingConfig.planName || "",
        intervals: pricingConfig.intervals || ["monthly"],
        tokenAllowance: safeNumber(pricingConfig.tokenAllowance),
        prices: (pricingConfig.prices || []).map((p) => ({
          ...p,
          monthlyAmount: safeNumber(p.monthlyAmount),
          yearlyAmount: safeNumber(p.yearlyAmount),
          amount: safeNumber(p.amount),
        })),
      };

      // 1. Save pricing template
      const pricingPayload = {
        applicationId: selectedApp,
        modelType: pricingModel,
        pricingConfig: {
          ...preparedPricingConfig,
          intervals: pricingConfig.intervals || ["monthly"],
        },
        freemiumPermissions:
          pricingModel === "freemium" ? freemiumPermissions : [],
        aiFeatures:
          metadataAiFeatures.length > 0 ? metadataAiFeatures : undefined,
      };

      // Add trial config for non-freemium models
      if (pricingModel !== "freemium") {
        pricingPayload.trialConfig = {
          enabled: trialEnabled,
          ...(trialEnabled && { days: trialDays }),
          permissions: trialEnabled ? trialPermissions : [],
        };
      }

      // Add token config for token-based model
      if (pricingModel === "token_based") {
        pricingPayload.tokenConfig = {
          allowance: safeNumber(pricingConfig.tokenAllowance) || 0,
          resetPeriod: "monthly",
        };
      }

      console.log("Saving pricing payload:", pricingPayload);

      // Save pricing template
      const pricingResponse = await api.post(
        "/pricing-templates",
        pricingPayload,
      );

      // 2. Save token settings (if applicable)
      if (tokenEnforcement !== "disabled") {
        const tokenPayload = {
          applicationId: selectedApp,
          tokenEnforcement,
        };

        if (tokenLimit !== "" && tokenLimit !== null) {
          tokenPayload.tokenLimit = parseInt(tokenLimit);
        }
        if (tokenResetPeriod) {
          tokenPayload.resetPeriod = tokenResetPeriod;
        }

        await api.put("/access/token-settings", tokenPayload);
      }

      setSuccess(true);
      setCurrentTemplate(pricingResponse.data.data.template);

      // Refresh template to show updated values
      await fetchCurrentTemplate(selectedApp);

      // Auto-hide success message
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error("Failed to save configuration", err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.details?.[0]?.msg ||
          "Failed to save configuration. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTrialPermissionsChange = (selectedPermissions) => {
    setTrialPermissions(selectedPermissions || []);
  };

  const handleFreemiumPermissionsChange = (selectedPermissions) => {
    setFreemiumPermissions(selectedPermissions || []);
  };

  if (loadingApps) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Pricing Configuration</h1>
          <p className="page-subtitle">
            Manage pricing, trials, tokens, and vouchers for your applications
          </p>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="alert alert-error">
          <FiAlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <FiCheck />
          <span>Configuration saved successfully!</span>
        </div>
      )}

      {/* Application Selector */}
      <div className="card">
        <label className="form-label">Select Application</label>
        <select
          value={selectedApp || ""}
          onChange={(e) => setSelectedApp(e.target.value)}
          className="form-input"
        >
          {applications.map((app) => (
            <option key={app.id} value={app.id}>
              {app.name || app.id}
              {app.active ? "" : " (Inactive)"}
            </option>
          ))}
        </select>

        {loadingTemplate && (
          <div className="loading-indicator">
            <div className="spinner small"></div>
            Loading current configuration...
          </div>
        )}

        {currentTemplate && !loadingTemplate && (
          <div className="alert info">
            <FiShield />
            <div>
              <div>
                <strong>Active Template:</strong>{" "}
                {formatModelType(currentTemplate.model_type)}
                {currentTemplate.trial_enabled &&
                  currentTemplate.trial_days && (
                    <span> | Trial: {currentTemplate.trial_days} days</span>
                  )}
              </div>
              {currentTemplate.configuration && (
                <div className="template-details">
                  {currentTemplate.configuration.tokenLimit && (
                    <span>
                      Token Limit:{" "}
                      {safeNumber(
                        currentTemplate.configuration.tokenLimit,
                      ).toLocaleString()}
                      /{currentTemplate.token_reset_period || "month"}
                    </span>
                  )}
                  {(currentTemplate.configuration.prices || []).map((p) => (
                    <span key={p.currency}>
                      {" "}
                      | {p.currency.toUpperCase()}:{" "}
                      {formatCurrency(p.monthlyAmount || p.amount, p.currency)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="tab-nav">
        <button
          className={`tab-btn ${activeTab === "pricing" ? "active" : ""}`}
          onClick={() => setActiveTab("pricing")}
        >
          <FiClock /> Pricing & Trials
        </button>
        <button
          className={`tab-btn ${activeTab === "tokens" ? "active" : ""}`}
          onClick={() => setActiveTab("tokens")}
        >
          <FiActivity /> Token Settings
        </button>
        <button
          className={`tab-btn ${activeTab === "vouchers" ? "active" : ""}`}
          onClick={() => setActiveTab("vouchers")}
        >
          <FiTag /> Discount Vouchers
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* PRICING TAB */}
        {activeTab === "pricing" && (
          <>
            <div className="card">
              {pricingModel && pricingModel !== "freemium" && (
                <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                  <label className="form-label">Plan Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. KiPlaylist Pro"
                    value={pricingConfig.planName || ""}
                    onChange={(e) =>
                      setPricingConfig((prev) => ({
                        ...prev,
                        planName: e.target.value,
                      }))
                    }
                  />
                  <p className="helper-text">
                    Overrides the auto-generated product name (leave blank to
                    use the application display name).
                  </p>
                </div>
              )}
              <PricingModelSelector
                selectedModel={pricingModel}
                onModelChange={setPricingModel}
                config={pricingConfig}
                onConfigChange={setPricingConfig}
              />
            </div>

            {/* Trial Configuration */}
            {pricingModel && pricingModel !== "freemium" && (
              <div className="card">
                <h3>
                  <FiClock /> Trial Configuration
                </h3>

                <div className="trial-config">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={trialEnabled}
                      onChange={(e) => setTrialEnabled(e.target.checked)}
                    />
                    <span>Enable Trial Period</span>
                  </label>

                  {trialEnabled && (
                    <>
                      <div className="form-group">
                        <label className="form-label">
                          Trial Duration (days)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={trialDays}
                          onChange={(e) =>
                            setTrialDays(parseInt(e.target.value) || 14)
                          }
                          className="form-input"
                          style={{ maxWidth: "200px" }}
                        />
                      </div>

                      <div>
                        <label className="form-label">Trial Permissions</label>
                        <p className="helper-text">
                          Select permissions for trial users from auth service
                        </p>
                        <PermissionsSelector
                          permissionsByCategory={permissionsByCategory}
                          selectedPermissions={trialPermissions}
                          onChange={handleTrialPermissionsChange}
                          loading={loadingPermissions}
                          aiPermissions={aiPermissions}
                          showAIIndicator={true}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Freemium Permissions */}
            {pricingModel === "freemium" && (
              <div className="card">
                <h3>Free Tier Permissions</h3>
                <p className="helper-text">
                  Select permissions for free tier from auth service
                </p>
                <PermissionsSelector
                  permissionsByCategory={permissionsByCategory}
                  selectedPermissions={freemiumPermissions}
                  onChange={handleFreemiumPermissionsChange}
                  loading={loadingPermissions}
                  aiPermissions={aiPermissions}
                  showAIIndicator={true}
                />
              </div>
            )}

            {/* AI Features Info */}
            {pricingModel === "token_based" && aiPermissions.length > 0 && (
              <div className="alert warning">
                <FiZap />
                <div>
                  <h4>Detected AI Features ({aiPermissions.length})</h4>
                  <p>These permissions will automatically consume tokens:</p>
                  <div className="badge-container">
                    {aiPermissions.map((perm) => (
                      <span key={perm} className="badge badge-warning">
                        <FiZap /> {perm}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* TOKEN SETTINGS TAB */}
        {activeTab === "tokens" && selectedApp && (
          <div className="card">
            <h3>
              <FiActivity /> Token Usage & Limits
            </h3>

            <p className="helper-text">
              Control how AI token usage is tracked and enforced for all users
            </p>

            <div className="info-box">
              <FiShield />
              <span>
                <strong>Source of Truth:</strong> Token limits are read from the
                active pricing template. Changes here update the template and
                auto-sync to all usage limits and balances.
              </span>
            </div>

            {/* Enforcement Mode */}
            <div className="enforcement-section">
              <label className="form-label">Enforcement Mode</label>
              <div className="radio-group">
                {[
                  {
                    value: "track_only",
                    label: "Track Only",
                    desc: "Record all AI usage for analytics, but never block users. Recommended for starting out.",
                  },
                  {
                    value: "enforce",
                    label: "Enforce Limits",
                    desc: "Block requests when a user's token balance reaches zero. Users see their remaining balance.",
                  },
                  {
                    value: "disabled",
                    label: "Disabled",
                    desc: "Don't track or enforce token usage at all. Token section is hidden from users.",
                  },
                ].map((mode) => (
                  <label
                    key={mode.value}
                    className={`radio-card ${tokenEnforcement === mode.value ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="tokenEnforcement"
                      value={mode.value}
                      checked={tokenEnforcement === mode.value}
                      onChange={(e) => setTokenEnforcement(e.target.value)}
                    />
                    <div>
                      <div className="radio-label">{mode.label}</div>
                      <div className="radio-desc">{mode.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Limit & Reset */}
            {tokenEnforcement !== "disabled" && (
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">
                    Token Limit Per Period
                    {tokenEnforcement === "track_only" && (
                      <span className="label-note">
                        (display only, not enforced)
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={tokenLimit}
                    onChange={(e) => setTokenLimit(e.target.value)}
                    placeholder="e.g. 10000"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Reset Period</label>
                  <select
                    value={tokenResetPeriod}
                    onChange={(e) => setTokenResetPeriod(e.target.value)}
                    className="form-input"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="never">Never (lifetime)</option>
                  </select>
                </div>
              </div>
            )}

            {/* AI Features — which permissions consume tokens */}
            {tokenEnforcement !== "disabled" && (
              <div className="form-group" style={{ marginTop: "1.5rem" }}>
                <label className="form-label">
                  <FiZap
                    style={{ marginRight: "6px", color: "var(--warning)" }}
                  />
                  AI Features (Token Consumers)
                </label>
                <p className="helper-text">
                  These permissions deduct from users' token balances when used.
                  Auto-detected features (matching AI patterns) are shown with a
                  ⚡ indicator.
                </p>

                {/* Current features list */}
                <div
                  className="badge-container"
                  style={{ marginBottom: "0.75rem", minHeight: "36px" }}
                >
                  {metadataAiFeatures.length === 0 ? (
                    <span
                      className="helper-text"
                      style={{ fontStyle: "italic" }}
                    >
                      No AI features configured — will auto-detect on save.
                    </span>
                  ) : (
                    metadataAiFeatures.map((feat) => {
                      const isAutoDetected = aiPermissions.includes(feat);
                      return (
                        <span
                          key={feat}
                          className="badge badge-warning"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {isAutoDetected && <FiZap size={11} />}
                          {feat}
                          <button
                            onClick={() =>
                              setMetadataAiFeatures((prev) =>
                                prev.filter((f) => f !== feat),
                              )
                            }
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "0 0 0 2px",
                              color: "inherit",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title={`Remove ${feat}`}
                          >
                            <FiX size={11} />
                          </button>
                        </span>
                      );
                    })
                  )}
                </div>

                {/* Add feature dropdown */}
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <select
                    value={aiFeatureToAdd}
                    onChange={(e) => setAiFeatureToAdd(e.target.value)}
                    className="form-input"
                    style={{ flex: 1 }}
                  >
                    <option value="">— select a permission to add —</option>
                    {allPermissions
                      .filter((p) => !metadataAiFeatures.includes(p.name))
                      .map((p) => (
                        <option key={p.name} value={p.name}>
                          {p.name}
                          {aiPermissions.includes(p.name) ? " ⚡" : ""}
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={() => {
                      if (
                        aiFeatureToAdd &&
                        !metadataAiFeatures.includes(aiFeatureToAdd)
                      ) {
                        setMetadataAiFeatures((prev) => [
                          ...prev,
                          aiFeatureToAdd,
                        ]);
                        setAiFeatureToAdd("");
                      }
                    }}
                    disabled={!aiFeatureToAdd}
                    className="btn btn-secondary"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <FiPlus /> Add
                  </button>
                </div>

                {/* Quick-add auto-detected features */}
                {aiPermissions.filter((p) => !metadataAiFeatures.includes(p))
                  .length > 0 && (
                  <div style={{ marginTop: "0.5rem" }}>
                    <button
                      onClick={() =>
                        setMetadataAiFeatures((prev) => [
                          ...prev,
                          ...aiPermissions.filter((p) => !prev.includes(p)),
                        ])
                      }
                      className="btn btn-secondary btn-sm"
                    >
                      <FiZap /> Add all auto-detected (
                      {
                        aiPermissions.filter(
                          (p) => !metadataAiFeatures.includes(p),
                        ).length
                      }
                      )
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* VOUCHERS TAB */}
        {activeTab === "vouchers" && (
          <div className="card">
            <div className="voucher-header">
              <div>
                <h3>
                  <FiTag /> Discount Vouchers
                </h3>
                <p className="helper-text">
                  Create discount codes for your customers
                </p>
              </div>
              <button
                onClick={() => setShowVoucherForm(!showVoucherForm)}
                className={`btn ${showVoucherForm ? "btn-secondary" : "btn-primary"}`}
              >
                {showVoucherForm ? (
                  "Cancel"
                ) : (
                  <>
                    <FiPlus /> New Voucher
                  </>
                )}
              </button>
            </div>

            {/* Create Voucher Form */}
            {showVoucherForm && (
              <div className="voucher-form">
                <h4>Create New Voucher</h4>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Voucher Code</label>
                    <div className="code-input-group">
                      <input
                        type="text"
                        value={newVoucher.code}
                        onChange={(e) =>
                          setNewVoucher((v) => ({
                            ...v,
                            code: e.target.value.toUpperCase(),
                          }))
                        }
                        placeholder="AUTO-GENERATED"
                        className="form-input"
                      />
                      <button
                        onClick={() =>
                          setNewVoucher((v) => ({
                            ...v,
                            code: generateVoucherCode(),
                          }))
                        }
                        className="btn btn-secondary btn-sm"
                        title="Generate code"
                      >
                        <FiPercent />
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Discount Type</label>
                    <select
                      value={newVoucher.discountType}
                      onChange={(e) =>
                        setNewVoucher((v) => ({
                          ...v,
                          discountType: e.target.value,
                        }))
                      }
                      className="form-input"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed_amount">Fixed Amount</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Discount Value</label>
                    <input
                      type="number"
                      min="1"
                      max={
                        newVoucher.discountType === "percentage"
                          ? 100
                          : undefined
                      }
                      value={newVoucher.discountValue}
                      onChange={(e) =>
                        setNewVoucher((v) => ({
                          ...v,
                          discountValue: parseInt(e.target.value) || 0,
                        }))
                      }
                      className="form-input"
                    />
                    <small className="field-note">
                      {newVoucher.discountType === "percentage"
                        ? "Percentage off (1-100)"
                        : "Amount off in cents (e.g., 1000 for $10)"}
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Max Redemptions</label>
                    <input
                      type="number"
                      min="1"
                      value={newVoucher.maxRedemptions || ""}
                      onChange={(e) =>
                        setNewVoucher((v) => ({
                          ...v,
                          maxRedemptions: e.target.value
                            ? parseInt(e.target.value)
                            : null,
                        }))
                      }
                      placeholder="Unlimited"
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Valid From</label>
                    <input
                      type="date"
                      value={newVoucher.valid_from}
                      onChange={(e) =>
                        setNewVoucher((v) => ({
                          ...v,
                          valid_from: e.target.value,
                        }))
                      }
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Valid Until</label>
                    <input
                      type="date"
                      value={newVoucher.valid_until}
                      onChange={(e) =>
                        setNewVoucher((v) => ({
                          ...v,
                          valid_until: e.target.value,
                        }))
                      }
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    onClick={() => setShowVoucherForm(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button onClick={createVoucher} className="btn btn-primary">
                    <FiPlus /> Create Voucher
                  </button>
                </div>
              </div>
            )}

            {/* Vouchers List */}
            {vouchersLoading ? (
              <div className="loading-container">
                <div className="spinner"></div>
              </div>
            ) : vouchers.length === 0 ? (
              <div className="empty-state">
                <FiTag className="empty-icon" />
                <p>No vouchers created yet</p>
                <p className="small">Create your first discount code above</p>
              </div>
            ) : (
              <div className="vouchers-list">
                {vouchers.map((voucher) => (
                  <div key={voucher.id} className="voucher-item">
                    <div className="voucher-info">
                      <div className="voucher-details">
                        <code className="voucher-code">{voucher.code}</code>
                        <span
                          className={`badge ${voucher.active ? "badge-success" : "badge-warning"}`}
                        >
                          {voucher.active ? "Active" : "Inactive"}
                        </span>
                        <span className="voucher-value">
                          {voucher.discount_type === "percentage"
                            ? `${voucher.discount_value}% off`
                            : `$${(voucher.discount_value / 100).toFixed(2)} off`}
                        </span>
                        {(voucher.redemptions > 0 ||
                          voucher.redemption_count > 0) && (
                          <span className="voucher-usage">
                            Used:{" "}
                            {voucher.redemptions ||
                              voucher.redemption_count ||
                              0}
                            {voucher.max_redemptions
                              ? `/${voucher.max_redemptions}`
                              : ""}
                          </span>
                        )}
                      </div>
                      {(voucher.valid_from || voucher.valid_until) && (
                        <div className="voucher-dates">
                          {voucher.valid_from && (
                            <span>From {formatDate(voucher.valid_from)}</span>
                          )}
                          {voucher.valid_from && voucher.valid_until && " - "}
                          {voucher.valid_until && (
                            <span>Until {formatDate(voucher.valid_until)}</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="voucher-actions">
                      <button
                        onClick={() => copyToClipboard(voucher.code)}
                        className="btn-icon"
                        title="Copy code"
                      >
                        {copiedCode === voucher.code ? <FiCheck /> : <FiCopy />}
                      </button>
                      <button
                        onClick={() => toggleVoucherActive(voucher)}
                        className={`btn-sm ${voucher.active ? "btn-secondary" : "btn-success"}`}
                      >
                        {voucher.active ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        onClick={() => deleteVoucher(voucher.id)}
                        className="btn-icon"
                        title="Delete voucher"
                      >
                        <FiTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* SINGLE SAVE BUTTON - Everything in one place */}
      <div className="save-section">
        <button
          onClick={handleSave}
          disabled={saving || !pricingModel}
          className="btn btn-primary btn-large"
        >
          {saving ? (
            <>
              <div className="spinner small"></div>
              Saving All Changes...
            </>
          ) : (
            <>
              <FiSave />
              Save All Configuration
            </>
          )}
        </button>
        <p className="save-note">
          Saves pricing model, trial settings, token configuration, and syncs
          everything
        </p>
      </div>
    </div>
  );
}

export default SimplifiedPaymentConfig;
